import { Box, Grid } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Logo from 'assets/logo.png'
import { useAppSelector } from 'store/useStoreHook'
import { useDispatch } from 'react-redux'
import SplashScreenPage from 'pages/SplashPage'
import { ClientType, SelectClientContract } from 'models'
import { useHistory, useLocation } from 'react-router-dom'
import * as yup from 'yup'
import queryString from 'query-string'
import React from 'react'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import clientService, { IRegisterParam } from 'Services/api/client.service'
import WifiStatus from 'components/WifiStatus/WifiStatus'
import { LocationSwitcher } from 'views/Login/LocationSwitcher/LocationSwitcher'
import LoadFailedPage from 'pages/LoadFailedPage'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import RegisterOrSelectDeviceTab from './RegisterOrSelectDeviceTab'
import { fetchWithNotification } from 'store/actions/base.action'
import authService from 'Services/api/auth.service'
import { finalizeUserLoginFlow, setClientTokenKey } from 'store/authCommon'
import { isUnauthorizedResult, showErrorMessage } from 'store/actions/ui.action'
import ROUTE from 'constant/route'
import { AuthorizationState } from 'store/models/authModels'
import { isEmpty, isError } from 'lodash-es'
import { isExceedLogin } from 'utilities/commonError'
import ExceedLoginWarningDialog from './ExceedLoginWarningDialog'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import httpRequest from 'httpRequest'
import errorMessages from 'utilities/errorMessages'

export interface IRegisterDeviceForm {
    name: string
    clientType: ClientType
    token?: string
}

export enum RegisterDeviceTab {
    EXISTING = 'EXISTING',
    REGISTER = 'REGISTER',
}

interface TabPanelProps {
    children?: React.ReactNode
    dir?: string
    index: RegisterDeviceTab
    value: RegisterDeviceTab
}

export function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            sx={{
                backgroundColor: '#60D3B1',
            }}
            {...other}
            width="100%"
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </Box>
    )
}

function RegisterDevicePageContainer({
    clients,
}: {
    clients: SelectClientContract[]
}) {
    const dispatch = useDispatch()
    const { search } = useLocation()
    const { cancelable } = queryString.parse(search)

    const history = useHistory()
    const { isLandscapeMobile, isMobile } = useScreenSize()

    const currentClient = useAppSelector((state) => state.client.currentClient)
    const [processing, setProcessing] = useState(false)

    const [currentTab, setCurrentTab] = useState<RegisterDeviceTab>(
        isEmpty(clients)
            ? RegisterDeviceTab.REGISTER
            : RegisterDeviceTab.EXISTING,
    )

    const [openExceedLoginDialog, setOpenExceedLoginDialog] = useState(false)

    const validationSchema = useMemo(() => {
        if (currentTab === RegisterDeviceTab.EXISTING)
            return yup.object().shape({
                token: yup.string().required('Please select a device'),
            })

        return yup.object().shape({
            name: yup.string().notOneOf(
                clients.map((a) => a.name),
                'Duplicate device name.',
            ),
        })
    }, [clients, currentTab])

    const getOrRegisterWebToken = useCallback(
        async (registerParam: IRegisterParam, token = '') => {
            setProcessing(true)
            try {
                let currentClient
                // If user select existing client
                if (token) {
                    setClientTokenKey(token)
                    const authResult = (
                        await authService.issueRealAccessToken({
                            forceLogout: false,
                        })
                    ).data
                    await finalizeUserLoginFlow(authResult, dispatch, history)
                }
                // if user decide to create new client
                else {
                    const result = await fetchWithNotification({
                        apiLinkFnc: () =>
                            clientService.registerWebClient(registerParam),
                    })
                    currentClient = result.data

                    setClientTokenKey(currentClient.tokenKey!)
                    const authResult = (
                        await authService.issueRealAccessToken({
                            forceLogout: false,
                        })
                    ).data
                    await finalizeUserLoginFlow(authResult, dispatch, history)
                }
            } catch (err) {
                // the temp token is expired then delete auth cookies and redirect to login page
                if (isExceedLogin(err)) {
                    setOpenExceedLoginDialog(true)
                } else if (isUnauthorizedResult(err)) {
                    const { message } = errorMessages(err)
                    if (
                        message ===
                        'login.error.terminal_duplicated_session_not_allowed'
                    ) {
                        dispatch(showErrorMessage(err))
                    } else {
                        // Unknown HTTP 401 Error, this is for edge-case (e.g., introduced new error message but not provided handler)
                        console.error(`Got Unhandled HTTP 401 with ${message}`)
                        httpRequest.deleteAuthorizationCookies()
                        history.replace(ROUTE.LOGIN)
                    }
                } else dispatch(showErrorMessage(err))
            }

            setProcessing(false)
        },
        [dispatch, history],
    )

    const handleCancel = useCallback(() => {
        history.replace(ROUTE.MAIN)
    }, [history])

    const initialForm: IRegisterDeviceForm = useMemo(
        () => ({
            name: '',
            clientType: clients.some(
                (c) => c.type === ClientType.PosTerminalWithOutCash,
            )
                ? ClientType.PosTerminalWithOutCash
                : ClientType.PosTerminalWithCash,
            token: '',
        }),
        [clients],
    )

    const form = useForm<IRegisterDeviceForm>({
        defaultValues: initialForm,
        resolver: yupResolver(validationSchema),
    })

    const resetForm = useCallback(
        (currentTab: RegisterDeviceTab) => {
            // use new form default client type to last used client type
            let type = ClientType.PosTerminalWithCash
            if (currentClient?.type === ClientType.OrderQuesTerminal)
                type = ClientType.OrderQuesTerminal
            else if (!currentClient?.posTerminalIsUseCashDrawer)
                type = ClientType.PosTerminalWithOutCash
            else type = ClientType.PosTerminalWithCash

            if (
                currentTab === RegisterDeviceTab.EXISTING &&
                clients.length > 0
            ) {
                form.reset({
                    name: currentClient?.name || '',
                    clientType: type,
                    token: currentClient?.tokenKey || clients[0].tokenKey || '',
                })
            } else {
                form.reset({
                    name: '',
                    clientType: type,
                    token: '',
                })
            }
        },
        [
            clients,
            currentClient?.name,
            currentClient?.posTerminalIsUseCashDrawer,
            currentClient?.tokenKey,
            currentClient?.type,
            form,
        ],
    )

    const onSubmit = useCallback(
        async (formValues: IRegisterDeviceForm) => {
            const type = formValues.clientType
            if (currentTab === RegisterDeviceTab.REGISTER) {
                await getOrRegisterWebToken({
                    clientType: type,
                    clientName: formValues.name,
                })
            } else
                await getOrRegisterWebToken(
                    {
                        clientType: type,
                        clientName: formValues.name,
                    },
                    formValues.token,
                )
        },
        [currentTab, getOrRegisterWebToken],
    )

    const handleChangeTab = useCallback(
        (event: React.SyntheticEvent, newTab: RegisterDeviceTab) => {
            setCurrentTab(newTab)
            resetForm(newTab)
        },
        [resetForm],
    )

    return (
        <React.Fragment>
            <Grid
                container
                sx={{
                    position: 'absolute',
                    bottom: '0',
                    top: '0',
                    right: '0',
                    left: '0',
                    background:
                        'radial-gradient(circle, rgba(90, 223, 180, 1) 0%, rgba(18, 174, 133, 1) 50%, rgba(16, 172, 132, 1) 100%)',
                    textAlign: 'center',
                    height: '100vh',
                }}
                position={'relative'}
            >
                <Grid
                    item
                    container
                    justifyContent="space-between"
                    alignItems="flex-start"
                    m={0}
                    position="absolute"
                    top={5}
                >
                    <Grid item>
                        <Box p={0.5}>
                            <LocationSwitcher />
                        </Box>
                    </Grid>
                    <Grid item>
                        <WifiStatus />
                    </Grid>
                </Grid>
                <Grid
                    item
                    container
                    sx={{
                        transform: isLandscapeMobile
                            ? 'scale(0.75) translate(-67%, -67%)'
                            : 'scale(1) translate(-50%, -50%)',
                    }}
                    position={'absolute'}
                    top={'50%'}
                    left={'50%'}
                >
                    <Grid
                        item
                        xs={12}
                        container
                        justifyContent="center"
                        alignItems="flex-end"
                        pb={isLandscapeMobile ? 0 : 3}
                    >
                        <Grid item>
                            <Box component="img" src={Logo} alt="Logo" />
                        </Grid>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        container
                        justifyContent="center"
                        alignItems="flex-start"
                        mx={2}
                    >
                        <Grid item>
                            <Grid
                                item
                                xs
                                style={{
                                    /**
                                     * Note
                                     * The minimal mobile screen resolution is 320px in width that is suitable for iPhone 5SE.
                                     * https://www.altamira.ai/blog/common-screen-sizes-for-responsive-web-design/
                                     * */
                                    maxWidth: isMobile ? 320 : 400,
                                }}
                            >
                                <FormProvider {...form}>
                                    <form
                                        onSubmit={form.handleSubmit(onSubmit)}
                                    >
                                        <RegisterOrSelectDeviceTab
                                            currentTab={currentTab}
                                            handleChangeTab={handleChangeTab}
                                            clients={clients}
                                            disabled={processing}
                                            cancelable={Boolean(cancelable)}
                                            handleCancel={handleCancel}
                                        />
                                    </form>
                                </FormProvider>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {openExceedLoginDialog && (
                <ExceedLoginWarningDialog
                    onClose={() => setOpenExceedLoginDialog(false)}
                />
            )}
        </React.Fragment>
    )
}

function RegisterDevicePage() {
    const history = useHistory()
    const authState = useAppSelector((state) => state.auth.authState)

    const [clients = [], getAllClients, status] = useFetchApi(
        clientService.getClients,
    )

    // this is need to prevent unintentionally rendering of <RegisterDevicePageContainer />
    // In extreme case, user's session has been taken or intentionally removed, but the child component will be rendered in first rendering frame
    // because `useEffect()` may activate in later frame
    const isStillNotAuthorized = useMemo(
        () =>
            authState !== AuthorizationState.SUCCESS_NON_CLIENT &&
            authState !== AuthorizationState.SUCCESS_REAL_CLIENT,
        [authState],
    )

    useEffect(() => {
        if (
            authState === AuthorizationState.SUCCESS_NON_CLIENT ||
            authState === AuthorizationState.SUCCESS_REAL_CLIENT
        )
            getAllClients()
    }, [authState, getAllClients])

    useEffect(() => {
        if (isStillNotAuthorized) history.replace(ROUTE.LOGIN)
    }, [history, isStillNotAuthorized])

    if (isLoading(status)) return <SplashScreenPage />
    if (isError(status)) return <LoadFailedPage />

    // to prevent child component to be rendered in case of unauthorization
    if (isStillNotAuthorized) return <SplashScreenPage />

    return <RegisterDevicePageContainer clients={clients} />
}

export default RegisterDevicePage
